import React, { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import { Alert, App, Button, Card, Form, Input, Spin, Tabs } from 'antd'
import { LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { consumeSellerNotice, type SellerUiNotice } from '../utils/sellerApi'
import { useSellerAuth } from '../contexts/SellerAuthContext'

const resolveApiErrorMessage = (error: unknown, fallback: string) => {
  if (isAxiosError(error)) {
    const apiMessage = (error.response?.data as { message?: string } | undefined)?.message
    return apiMessage || fallback
  }

  return fallback
}

const SellerLoginPage = () => {
  const [sellerForm] = Form.useForm<{ business_email: string; password: string }>()
  const [staffForm] = Form.useForm<{ email: string; otp?: string }>()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'seller' | 'staff'>('seller')
  const [staffOtpRequested, setStaffOtpRequested] = useState(false)
  const [staffOtpEmail, setStaffOtpEmail] = useState<string | null>(null)
  const [notice, setNotice] = useState<SellerUiNotice | null>(null)
  const { login, requestStaffOtp, verifyStaffOtp } = useSellerAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as { message?: string } | null
  const { message } = App.useApp()

  useEffect(() => {
    setNotice(consumeSellerNotice())
  }, [])

  const alertDescription =
    state?.message ||
    'After admin approval, sign in with the password you created during registration. If no plan is active yet, you will be taken to seller plan activation before dashboard access is unlocked.'

  const sellerQuote = '"Great stores are built one fulfilled promise at a time."'

  const finishLogin = (hasOperationalAccess: boolean, successMessage: string) => {
    if (hasOperationalAccess) {
      message.success(successMessage)
      navigate('/seller/dashboard')
      return
    }

    message.info('Login successful. Activate a plan to unlock dashboard access.')
    navigate('/seller/plan-activation')
  }

  const onSellerLogin = async (values: { business_email: string; password: string }) => {
    setLoading(true)

    try {
      const seller = await login(values.business_email, values.password)
      finishLogin(Boolean(seller.access_state?.has_operational_access), 'Login successful! Welcome to the seller dashboard.')
    } catch (error: unknown) {
      message.error(resolveApiErrorMessage(error, 'Login failed'))
    } finally {
      setLoading(false)
    }
  }

  const onRequestStaffOtp = async () => {
    try {
      const values = await staffForm.validateFields(['email'])
      const normalizedEmail = values.email.trim().toLowerCase()

      setLoading(true)
      const responseMessage = await requestStaffOtp(normalizedEmail)

      setStaffOtpRequested(true)
      setStaffOtpEmail(normalizedEmail)
      staffForm.setFieldValue('email', normalizedEmail)
      message.success(responseMessage)
    } catch (error: unknown) {
      const fallback = 'Failed to send OTP'
      message.error(resolveApiErrorMessage(error, fallback))
    } finally {
      setLoading(false)
    }
  }

  const onVerifyStaffOtp = async (values: { email: string; otp?: string }) => {
    if (!staffOtpRequested) {
      await onRequestStaffOtp()
      return
    }

    setLoading(true)

    try {
      const seller = await verifyStaffOtp(values.email.trim().toLowerCase(), values.otp?.trim() ?? '')
      finishLogin(Boolean(seller.access_state?.has_operational_access), 'Staff login successful. Welcome to the seller workspace.')
    } catch (error: unknown) {
      message.error(resolveApiErrorMessage(error, 'OTP verification failed'))
    } finally {
      setLoading(false)
    }
  }

  const switchToSellerTab = () => {
    setActiveTab('seller')
    setStaffOtpRequested(false)
    setStaffOtpEmail(null)
    staffForm.resetFields()
  }

  return (
    <div className="seller-auth-page">
      <div className="seller-auth-shell seller-auth-shell--login">
        <div className="seller-auth-copy seller-auth-copy--login">
          <p className="seller-auth-eyebrow">Seller workspace</p>
          <h1>Operate your business with clarity, speed, and confidence.</h1>
          <p>
            Sign in to manage products, inventory, orders, billing, team members, and support from
            one streamlined seller control center.
          </p>

          <blockquote className="seller-auth-quote">
            {sellerQuote}
            <cite>Trendify Seller Principle</cite>
          </blockquote>

          <ul className="seller-auth-points">
            <li>Real-time visibility of catalog, stock, and order performance.</li>
            <li>Smart billing and plan access controls for smooth growth.</li>
            <li>Fast support workflow with ticket updates and responses.</li>
          </ul>
        </div>

        <div className="seller-auth-panel">
          <Spin spinning={loading}>
            <Card className="seller-auth-card seller-auth-card--login" title="Seller Login">
              {notice ? (
                <Alert
                  title={notice.title}
                  description={notice.description}
                  type={notice.type}
                  showIcon
                  closable
                  onClose={() => setNotice(null)}
                  style={{ marginBottom: 16 }}
                />
              ) : null}

              <Alert
                title="Welcome back to Trendify Seller Dashboard"
                description={alertDescription}
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                  if (key === 'seller') {
                    switchToSellerTab()
                    return
                  }

                  setActiveTab('staff')
                  sellerForm.resetFields()
                }}
                items={[
                  {
                    key: 'seller',
                    label: 'Seller Login',
                    children: (
                      <Form form={sellerForm} layout="vertical" onFinish={onSellerLogin}>
                        <Form.Item
                          name="business_email"
                          label="Business Email"
                          rules={[{ required: true, type: 'email', message: 'Valid business email is required' }]}
                        >
                          <Input prefix={<MailOutlined />} placeholder="yourbusiness@company.com" size="large" />
                        </Form.Item>

                        <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
                          <Input.Password prefix={<LockOutlined />} placeholder="Enter password" size="large" />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0 }}>
                          <Button type="primary" htmlType="submit" size="large" block style={{ height: 50 }}>
                            Login to Seller Workspace
                          </Button>
                        </Form.Item>
                      </Form>
                    ),
                  },
                  {
                    key: 'staff',
                    label: 'Staff OTP Login',
                    children: (
                      <>
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginBottom: 16 }}
                          title="Staff sign-in uses OTP"
                          description="Enter your staff email to receive a one-time password. No staff password is required."
                        />

                        {staffOtpRequested ? (
                          <Alert
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                            title="OTP sent"
                            description={`We sent an OTP to ${staffOtpEmail}.`}
                          />
                        ) : null}

                        <Form form={staffForm} layout="vertical" onFinish={onVerifyStaffOtp}>
                          <Form.Item
                            name="email"
                            label="Staff Email"
                            rules={[{ required: true, type: 'email', message: 'Valid staff email is required' }]}
                          >
                            <Input prefix={<MailOutlined />} placeholder="staff@company.com" size="large" disabled={staffOtpRequested} />
                          </Form.Item>

                          {staffOtpRequested ? (
                            <Form.Item
                              name="otp"
                              label="One-Time Password"
                              rules={[
                                { required: true, message: 'OTP is required' },
                                { len: 6, message: 'OTP must be 6 digits' },
                              ]}
                            >
                              <Input
                                prefix={<SafetyOutlined />}
                                maxLength={6}
                                size="large"
                                placeholder="Enter 6-digit OTP"
                              />
                            </Form.Item>
                          ) : null}

                          <Form.Item style={{ marginBottom: 0 }}>
                            <div style={{ display: 'grid', gap: 10 }}>
                              {!staffOtpRequested ? (
                                <Button type="primary" size="large" block style={{ height: 50 }} onClick={() => void onRequestStaffOtp()}>
                                  Send OTP
                                </Button>
                              ) : (
                                <>
                                  <Button type="primary" htmlType="submit" size="large" block style={{ height: 50 }}>
                                    Verify OTP and Login
                                  </Button>
                                  <Button block onClick={() => void onRequestStaffOtp()}>
                                    Resend OTP
                                  </Button>
                                  <Button
                                    block
                                    onClick={() => {
                                      setStaffOtpRequested(false)
                                      setStaffOtpEmail(null)
                                      staffForm.setFieldValue('otp', undefined)
                                    }}
                                  >
                                    Use Different Email
                                  </Button>
                                </>
                              )}
                            </div>
                          </Form.Item>
                        </Form>
                      </>
                    ),
                  },
                ]}
              />

              <div className="seller-auth-footer-text">
                Need approval first? <Link to="/seller/register">Create a seller request</Link>
              </div>
            </Card>
          </Spin>
        </div>
      </div>
    </div>
  )
}

export default SellerLoginPage
